/**
 * The review reasons a staff surface may show for an eligibility check.
 *
 * `eligibility_checks.resolution.reviewReason` is persisted as an arbitrary
 * string: most values come from an upstream matcher's wire, and nothing in the
 * schema stops a future matcher from writing a free-text explanation that names
 * the member. A shape check would not help, because a value such as `jane_doe`
 * looks exactly like a code. So a staff read passes a reason through only when
 * it is one of the codes the pipeline is known to produce, and reports anything
 * else as `unrecognised`.
 *
 * Adding a code is deliberate: a new upstream reason reads `unrecognised` until
 * someone has checked it carries no member detail and listed it here.
 */
export const ELIGIBILITY_REVIEW_REASON_CODES = [
  // Matcher outcomes (internaltools and the local fakes).
  'no_candidates',
  'no_match',
  'multi_client_match',
  'postal_code_mismatch',
  'unknown_client',
  'needs_manual_review',
  // Verifier outcomes, written when the check could not be resolved.
  'client_ref_missing',
  'programs_unresolved',
  'programs_disjoint',
  'provider_failed',
  'publish_failed',
  'upstream_timeout',
  // Migration outcomes, written by the Tier 0 import.
  'migration_unlinked',
] as const;

export type EligibilityReviewReasonCode = (typeof ELIGIBILITY_REVIEW_REASON_CODES)[number];

export const ELIGIBILITY_REVIEW_REASON_UNRECOGNISED = 'unrecognised';

/** Every value a staff read may carry in a `reviewReason` field (besides null). */
export const ELIGIBILITY_STAFF_REVIEW_REASON = [
  ...ELIGIBILITY_REVIEW_REASON_CODES,
  ELIGIBILITY_REVIEW_REASON_UNRECOGNISED,
] as const;

export type EligibilityStaffReviewReason = (typeof ELIGIBILITY_STAFF_REVIEW_REASON)[number];

const isReviewReasonCode = (value: string): value is EligibilityReviewReasonCode =>
  ELIGIBILITY_REVIEW_REASON_CODES.some((code) => code === value);

/**
 * A stored review reason as a staff surface may show it: a listed code
 * unchanged, `null` unchanged, anything else `unrecognised`. Exact match only,
 * so case or punctuation variants of a code are not admitted either.
 */
export function toStaffReviewReason(raw: string | null): EligibilityStaffReviewReason | null {
  if (raw === null) return null;
  return isReviewReasonCode(raw) ? raw : ELIGIBILITY_REVIEW_REASON_UNRECOGNISED;
}
